import type { GeneratedFileSyncRequest } from './generatedFileLocalization';

export type DesktopGeneratedFileCommand = {
  user_id: number;
  project_id: string;
  space_id?: string | null;
  payload: { relative_path?: unknown; application?: unknown };
};

export type DesktopGeneratedFileHost = {
  saveGeneratedRemoteFile: (
    request: GeneratedFileSyncRequest
  ) => Promise<{ success: boolean; filePath?: string; error?: string }>;
  openInIDE: (
    filePath: string,
    ide: 'vscode'
  ) => Promise<{ success: boolean; error?: string }>;
};

const EDITABLE_EXTENSIONS = new Set([
  'html',
  'css',
  'js',
  'json',
  'md',
  'txt',
  'py',
  'ts',
  'tsx',
]);

export function parseGeneratedDesktopFileRequest(
  payload: DesktopGeneratedFileCommand['payload']
) {
  const relativePath = payload.relative_path;
  const application = payload.application;
  if (
    typeof relativePath !== 'string' ||
    !/^task_[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_.-]+)+$/.test(relativePath) ||
    relativePath.split('/').some((part) => part === '.' || part === '..')
  ) {
    throw new Error('Generated file must use a task-scoped relative path');
  }
  const filename = relativePath.split('/').at(-1)!;
  const extension = filename.split('.').at(-1)?.toLowerCase() || '';
  if (!EDITABLE_EXTENSIONS.has(extension)) {
    throw new Error('Unsupported generated file type');
  }
  if (application !== 'vscode' && application !== 'preview') {
    throw new Error('Unsupported generated file application');
  }
  if (application === 'preview' && extension !== 'html') {
    throw new Error('Browser preview requires an HTML file');
  }
  return { relativePath, filename, extension, application };
}

export async function openGeneratedDesktopFile(
  command: DesktopGeneratedFileCommand,
  context: {
    api: DesktopGeneratedFileHost;
    backendBaseUrl: string;
    email: string;
    userId: number;
    token: string;
    spaceName?: string;
    projectName?: string;
    openPreview: (file: FileInfo) => void;
  }
) {
  const file = parseGeneratedDesktopFileRequest(command.payload);
  if (
    !command.project_id ||
    !command.space_id ||
    command.user_id !== context.userId ||
    !context.email ||
    !context.token
  ) {
    throw new Error(
      'Generated file command does not belong to the signed-in project'
    );
  }
  const endpoint = new URL(context.backendBaseUrl);
  if (!['http:', 'https:'].includes(endpoint.protocol)) {
    throw new Error('Brain endpoint is unavailable');
  }
  const url = new URL(
    `${endpoint.pathname.replace(/\/$/, '')}/files/stream`,
    endpoint.origin
  );
  url.searchParams.set('path', file.relativePath);
  url.searchParams.set('project_id', command.project_id);
  url.searchParams.set('space_id', command.space_id);
  url.searchParams.set('email', context.email);
  url.searchParams.set('user_id', String(context.userId));

  const saved = await context.api.saveGeneratedRemoteFile({
    url: url.toString(),
    authorization: `Bearer ${context.token}`,
    filename: file.filename,
    relativePath: file.relativePath,
    accountName: context.email,
    spaceId: command.space_id,
    spaceName: context.spaceName,
    projectId: command.project_id,
    projectName: context.projectName,
    userId: context.userId,
  });
  if (!saved.success || !saved.filePath) {
    throw new Error(saved.error || 'Generated file could not be saved locally');
  }

  if (file.application === 'vscode') {
    const opened = await context.api.openInIDE(saved.filePath, 'vscode');
    if (!opened.success)
      throw new Error(opened.error || 'VS Code did not open');
  } else {
    context.openPreview({
      name: file.filename,
      type: file.extension,
      path: saved.filePath,
      relativePath: file.relativePath,
      isRemote: false,
      localPathAvailable: true,
    });
  }
  return { local_path: saved.filePath, opened_in: file.application };
}
